import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Drawer } from '@nextiva/next-ui';
import { submitData } from '@nextiva/credit-card-form';

import { PaymentDrawerHeader } from 'components';
import { CHARGE_SUCCESS_MESSAGE } from 'constant';
import {
  configObject,
  isNotEmptyObj,
  isDev,
  cardType,
  accountTypes,
  paymentOptions,
} from 'utils';
import PaymentDrawerContent from './PaymentDrawerContent/PaymentDrawerContent';
import GenericSpinner from 'UIElements/GenericSpinner/GenericSpinner';

import './PaymentDrawer.scss';

export const blockName = 'payment-drawer';

const PaymentDrawer = props => {
  const [iframeActions, setIframeActions] = useState({});
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [billingAddress1, setBillingAddress1] = useState('');
  const [billingAddress2, setBillingAddress2] = useState('');
  const [city, setCity] = useState('');
  const [usState, setUSState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [shouldBeDefault, setShouldBeDefault] = useState(true);
  const [paymentOption, setPaymentOption] = useState(
    paymentOptions.CREDIT_CARD
  );
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountType, setAccountType] = useState(accountTypes[0].label);
  const [showSpinner, setShowSpinner] = useState(false);

  const {
    startAddPaymentMethod,
    onClose,
    corpAcctNbr,
    setVantivResponse,
    vantivResponse,
    setVantivResponseEmpty,
    addGlobalError,
    paymentTypeObject,
    clearPaymentTypeObject,
    togglePaymentDrawer,
    startProcessNextivaBill,
    startAddDefaultPaymentMethod,
    startSetDefaultPayment,
    startAddNextivaPlan,
    startAddNextivaBill,
    startAddSummaryInvoices,
    startAddNextInvoice,
    billToSiteNumber,
    addAlertDetails,
    refreshBillingInfo,
    isSummaryPage,
  } = props;

  const mounted = useRef(false);

  const checkStartProcessNextivaBill = useCallback(() => {
    if (isNotEmptyObj(paymentTypeObject)) {
      return startProcessNextivaBill({
        ...paymentTypeObject,
        paymentType:
          paymentTypeObject.paymentType === 'other'
            ? 'usage'
            : paymentTypeObject.paymentType,
        billToSiteNumber: billToSiteNumber,
      });
    }
  }, [billToSiteNumber, startProcessNextivaBill, paymentTypeObject]);

  const checkShouldBeDefault = useCallback(() => {
    if (shouldBeDefault) {
      return startAddDefaultPaymentMethod(billToSiteNumber);
    } else if (isNotEmptyObj(paymentTypeObject)) {
      return startSetDefaultPayment();
    }
  }, [
    billToSiteNumber,
    shouldBeDefault,
    startAddDefaultPaymentMethod,
    startSetDefaultPayment,
    paymentTypeObject,
  ]);

  const processPaymentMethod = useCallback(async () => {
    const billingInfo = {
      address1: billingAddress1,
      address2: billingAddress2,
      city: city,
      firstName,
      isoCountryCode: 'US',
      lastName,
      postalCode: zipCode,
      state: usState,
    };

    let paymentMethodData;

    if (paymentOption === paymentOptions.CREDIT_CARD) {
      paymentMethodData = {
        cardType: cardType(vantivResponse.type),
        expirationDate: `${vantivResponse.expMonth}${vantivResponse.expYear}`,
        lastFourDigits: vantivResponse.lastFour,
        registrationId: vantivResponse.paypageRegistrationId,
        orderId: vantivResponse.orderId,
      };
    } else {
      paymentMethodData = {
        bankRoutingNumber: routingNumber,
        accountNumber,
        accountType,
        bankName,
      };
    }

    try {
      setShowSpinner(true);
      const errorAddPayment = await startAddPaymentMethod({
        billingInfo,
        ...paymentMethodData,
        corpAcctNbr,
        default: true,
        testMode: paymentOption === paymentOptions.ECHECK ? false : isDev,
        status: 0,
        paymentOption,
        billToSiteNumber,
      });

      const errorProcessBill = !errorAddPayment
        ? await checkStartProcessNextivaBill()
        : errorAddPayment;

      if (isSummaryPage) {
        await startAddNextivaBill(billToSiteNumber);
        await startAddSummaryInvoices(billToSiteNumber);
        await startAddNextivaPlan(billToSiteNumber);
      }

      await startAddNextInvoice(billToSiteNumber);
      await checkShouldBeDefault();
      await clearPaymentTypeObject();

      if (!isSummaryPage && !errorAddPayment) {
        addAlertDetails();
      }
      if (isSummaryPage && !errorProcessBill) {
        addAlertDetails({ message: CHARGE_SUCCESS_MESSAGE });
        refreshBillingInfo();
      }
    } finally {
      setShowSpinner(false);
      onClose();
    }
  }, [
    billToSiteNumber,
    billingAddress1,
    billingAddress2,
    city,
    firstName,
    lastName,
    zipCode,
    usState,
    paymentOption,
    vantivResponse.type,
    vantivResponse.expMonth,
    vantivResponse.expYear,
    vantivResponse.lastFour,
    vantivResponse.paypageRegistrationId,
    vantivResponse.orderId,
    corpAcctNbr,
    routingNumber,
    accountNumber,
    accountType,
    bankName,
    startAddPaymentMethod,
    checkStartProcessNextivaBill,
    startAddNextivaBill,
    startAddSummaryInvoices,
    startAddNextivaPlan,
    startAddNextInvoice,
    checkShouldBeDefault,
    clearPaymentTypeObject,
    addAlertDetails,
    onClose,
    refreshBillingInfo,
    isSummaryPage,
  ]);

  const callVantivRegistration = useCallback(() => {
    setShowSpinner(true);
    if (paymentOption === paymentOptions.CREDIT_CARD) {
      if (firstName && billingAddress1 && city && usState && zipCode) {
        window.eprotectIframeClient.getPaypageRegistrationId(submitData);
      }
    } else {
      processPaymentMethod();
    }
  }, [
    billingAddress1,
    city,
    firstName,
    paymentOption,
    processPaymentMethod,
    usState,
    zipCode,
  ]);

  const callback = useCallback(
    vantivResponse => {
      const {
        timeout,
        response,
        responseTime,
        message,
        ...data
      } = vantivResponse;
      if (timeout || Number(response) !== 870) {
        addGlobalError({ timeout, response, responseTime, message });
        setShowSpinner(false);
      } else {
        setVantivResponse(data);
      }
    },
    [setVantivResponse, addGlobalError]
  );

  const configDrawer = {
    ...configObject,
    callback,
  };

  const close = useCallback(() => {
    clearPaymentTypeObject();
    onClose();
  }, [clearPaymentTypeObject, onClose]);

  const backToPrev = useCallback(() => {
    togglePaymentDrawer();
    onClose();
  }, [onClose, togglePaymentDrawer]);

  const handlePaymentOptionChange = useCallback(
    event => setPaymentOption(event.target.value),
    [setPaymentOption]
  );

  const handleRoutingNumberChange = useCallback(
    (_, value) => setRoutingNumber(value),
    [setRoutingNumber]
  );

  const handleAccountNumberChange = useCallback(
    (_, value) => setAccountNumber(value),
    [setAccountNumber]
  );

  const handleBankNameChange = useCallback((_, value) => setBankName(value), [
    setBankName,
  ]);

  const handleAccountTypeChange = useCallback(
    ({ value }) => setAccountType(value),
    [setAccountType]
  );

  const handleFirstNameChange = useCallback((_, value) => setFirstName(value), [
    setFirstName,
  ]);

  const handleLastNameChange = useCallback((_, value) => setLastName(value), [
    setLastName,
  ]);

  const handleBillingAddress1Change = useCallback(
    (_, value) => setBillingAddress1(value),
    [setBillingAddress1]
  );

  const handleCityChange = useCallback((_, value) => setCity(value), [setCity]);

  const handleZipCodeChange = useCallback((_, value) => setZipCode(value), [
    setZipCode,
  ]);

  const handleDefaultCardChange = useCallback(
    event => setShouldBeDefault(event.target.value),
    [setShouldBeDefault]
  );

  const handleBillingAddress2Change = useCallback(
    (_, value) => setBillingAddress2(value),
    [setBillingAddress2]
  );

  const handleUSStateChange = useCallback(({ value }) => setUSState(value), [
    setUSState,
  ]);

  const onVantivActionsLoad = useCallback(
    actions => setIframeActions(actions),
    []
  );

  /**
   * Configuration object for payment method picker component.
   * Contains account data, handlers, and validation functions.
   */
  const paymentPickerConfig = {
    accountNumber,
    accountType,
    bankName,
    configDrawer,
    handleAccountNumberChange,
    handleAccountTypeChange,
    handleBankNameChange,
    handlePaymentOptionChange,
    handleRoutingNumberChange,
    onVantivActionsLoad,
    paymentOption,
    routingNumber,
  };

  /**
   * Configuration object for billing information component.
   * Contains address fields, personal data, and change handlers.
   */
  const billingInfoConfig = {
    billingAddress1,
    billingAddress2,
    city,
    firstName,
    handleBillingAddress1Change,
    handleBillingAddress2Change,
    handleCityChange,
    handleDefaultCardChange,
    handleFirstNameChange,
    handleLastNameChange,
    handleUSStateChange,
    handleZipCodeChange,
    lastName,
    shouldBeDefault,
    usState,
    zipCode,
  };

  /**
   * Configuration object for payment footer component.
   * Contains amount, registration handler, and close function.
   */
  const paymentFooterConfig = {
    amount: paymentTypeObject.amount,
    callVantivRegistration,
    close,
    submitButtonDisabled: showSpinner,
  };

  useEffect(() => {
    setVantivResponseEmpty();
    if (isNotEmptyObj(iframeActions)) {
      iframeActions.autoAdjustHeight();
      mounted.current = true;
    }
    if (mounted.current) {
      if (isNotEmptyObj(vantivResponse)) {
        processPaymentMethod();
      }
    }
  }, [
    iframeActions,
    vantivResponse,
    setVantivResponseEmpty,
    paymentTypeObject,
    processPaymentMethod,
  ]);

  return (
    <Drawer className={`${blockName}`} id={blockName} onClose={close}>
      <PaymentDrawerHeader
        shouldGoBack={isSummaryPage}
        backToPrev={backToPrev}
      />
      <div className={`${blockName}__drawer-content`}>
        {showSpinner && <GenericSpinner />}
        <PaymentDrawerContent
          billingInfoConfig={billingInfoConfig}
          isSummaryPage={isSummaryPage}
          paymentFooterConfig={paymentFooterConfig}
          paymentPickerConfig={paymentPickerConfig}
          showSpinner={showSpinner}
        />
      </div>
    </Drawer>
  );
};

PaymentDrawer.propTypes = {
  addGlobalError: PropTypes.func.isRequired,
  vantivResponse: PropTypes.object.isRequired,
  setVantivResponse: PropTypes.func.isRequired,
  setVantivResponseEmpty: PropTypes.func.isRequired,
  corpAcctNbr: PropTypes.number.isRequired,
  startAddPaymentMethod: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  clearPaymentTypeObject: PropTypes.func.isRequired,
  paymentTypeObject: PropTypes.object.isRequired,
  togglePaymentDrawer: PropTypes.func.isRequired,
  startProcessNextivaBill: PropTypes.func.isRequired,
  startAddDefaultPaymentMethod: PropTypes.func.isRequired,
  startSetDefaultPayment: PropTypes.func.isRequired,
  startAddNextivaPlan: PropTypes.func.isRequired,
  startAddNextivaBill: PropTypes.func.isRequired,
  startAddSummaryInvoices: PropTypes.func.isRequired,
  startAddNextInvoice: PropTypes.func.isRequired,
  billToSiteNumber: PropTypes.string.isRequired,
  addAlertDetails: PropTypes.func.isRequired,
  refreshBillingInfo: PropTypes.func.isRequired,
  isSummaryPage: PropTypes.bool.isRequired,
};

export default PaymentDrawer;
